<template>
    <button type="button" :class="classList" @click="clickable ? $emit('click') : null">
        <app-tooltip :value="tooltip" :disabled="!tooltip" class="app-chip__wrap">
            <span v-if="prefix || findSlot('prefix')" class="app-chip__prefix">
                <slot v-if="findSlot('prefix')" name="prefix" />
                <template v-else>{{ prefix }}</template>
            </span>

            <template v-if="loading">
                <slot v-if="findSlot('loader')" name="loader" class="app-chip__loader" />
                <v-progress-circular v-else indeterminate :width="4" class="app-chip__loader" />
            </template>

            <template v-if="!loading && (icon || findSlot('icon'))">
                <slot v-if="findSlot('icon')" name="icon" class="app-chip__icon" />
                <v-icon v-else-if="icon" class="app-chip__icon">{{ icon }}</v-icon>
            </template>

            <span v-if="text || findSlot('text') || findSlot('default')" class="app-chip__text">
                <slot v-if="findSlot('text')" name="text" />
                <slot v-else-if="findSlot('default')" name="default" />
                <template v-else>{{ text }}</template>
            </span>
        </app-tooltip>
    </button>
</template>

<script>
export default {
    name: 'AppChip',
    props: {
        type: {
            type: String,
            default: null,
            validator: value => {
                return ['primary', 'success', 'warning', 'error', 'info', 'grey'].includes(value);
            }
        },
        text: {
            type: String,
            default: null
        },
        prefix: {
            type: String,
            default: null
        },
        icon: {
            type: String,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        tooltip: {
            type: [String, Array],
            default: null
        },
        bold: {
            type: Boolean,
            default: false
        },
        solid: {
            type: Boolean,
            default: false
        },
        dark: {
            type: Boolean,
            default: false
        },
        clickable: {
            type: Boolean,
            default: false
        },

        // Size
        xSmall: {
            type: Boolean,
            default: false
        },
        small: {
            type: Boolean,
            default: false
        },
        large: {
            type: Boolean,
            default: false
        },
        xLarge: {
            type: Boolean,
            default: false
        },

        // Type
        primary: {
            type: Boolean,
            default: false
        },
        success: {
            type: Boolean,
            default: false
        },
        warning: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        info: {
            type: Boolean,
            default: false
        },
        grey: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        colour() {
            if (this.type) {
                return this.type;
            }

            switch (true) {
                case this.primary:
                    return 'primary';
                case this.success:
                    return 'success';
                case this.warning:
                    return 'warning';
                case this.error:
                    return 'error';
                case this.info:
                    return 'info';
                case this.grey:
                    return 'grey';
                default:
                    return null;
            }
        },
        size() {
            switch (true) {
                case this.xSmall:
                    return 'x-small';
                case this.small:
                    return 'small';
                case this.large:
                    return 'large';
                case this.xLarge:
                    return 'x-large';
                default:
                    return null;
            }
        },
        classList() {
            let classList = ['app-chip'];

            if (this.colour) {
                classList.push(`app-chip--${this.colour}`);
            }

            if (this.size) {
                classList.push(`app-chip--${this.size}`);
            }

            if (this.bold) {
                classList.push('app-chip--bold');
            }

            if (this.solid) {
                classList.push('app-chip--solid');
            }

            if (this.dark) {
                classList.push('app-chip--dark');
            }

            if (this.tooltip) {
                classList.push('app-chip--tooltip');
            }

            if (this.clickable) {
                classList.push('app-chip--clickable');
            }

            return classList.join(' ');
        }
    }
};
</script>

<style lang="scss">
.app-chip {
    display: inline-flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.7rem;
    min-width: 2.4em;
    user-select: none;
    line-height: 1.2;
    letter-spacing: 0;
    text-transform: initial;
    text-align: inherit;
    outline: none;
    cursor: default;
    &:focus {
        outline: none;
    }

    &--clickable {
        cursor: pointer;
    }

    &--primary {
        color: var(--v-primaryText-base);
    }

    &--success {
        color: var(--v-success-base);
        .app-chip__prefix,
        .app-chip__text,
        .app-chip__icon {
            filter: brightness(0.8);
        }
    }

    &--warning {
        color: var(--v-warning-base);
        .app-chip__prefix,
        .app-chip__text,
        .app-chip__icon {
            filter: brightness(0.7);
        }
    }

    &--error {
        color: var(--v-error-base);
        .app-chip__prefix,
        .app-chip__text,
        .app-chip__icon {
            filter: brightness(0.75);
        }
    }

    &--info {
        color: var(--v-info-base);
        .app-chip__prefix,
        .app-chip__text,
        .app-chip__icon {
            filter: brightness(0.9);
        }
    }

    &--grey {
        color: #6a6a6a;
    }

    &--x-small {
        font-size: 0.55rem;
    }

    &--small {
        font-size: 0.64rem;
    }

    &--large {
        font-size: 0.85rem;
    }

    &--x-large {
        font-size: 1.1rem;
    }

    &--tooltip {
        .app-chip__wrap > span {
            display: inline-flex;
            align-items: center;
        }
    }

    &__wrap {
        position: relative;
        display: inline-flex;
        align-items: center;
        border-radius: 3px;
        overflow: hidden;
        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: currentColor;
            pointer-events: none;
            opacity: 0.12;
            border-radius: inherit;
            z-index: 1;
        }
        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: white;
            z-index: 0;
            opacity: 0;
        }
    }

    &__text {
        color: inherit;
        font-size: 1em;
        padding: 0 0.4em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.5em;
        z-index: 2;
    }

    &__prefix {
        display: flex;
        align-items: center;
        position: relative;
        margin-right: -0.2em;
        white-space: nowrap;
        line-height: 1.5em;
        border-radius: 2px 0 0 2px;
        padding: 0 0.6rem 0 0.4em;
        z-index: 2;

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: currentColor;
            pointer-events: none;
            opacity: 0.1;
            border-radius: inherit;
            filter: brightness(0.9);
            clip-path: polygon(0% 100%, calc(100% - 0.5rem) 100%, 100% 0%, 0% 0%);
            z-index: 0;
        }
    }

    &__icon {
        margin-left: 0.4em;
        font-size: 0.9em !important;
        color: inherit !important;
    }

    &__loader {
        margin-left: 0.4em;
        height: 0.9em !important;
        width: 0.9em !important;
        align-self: center;
    }

    &--bold {
        .app-chip__text {
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
    }

    &--solid {
        .app-chip__wrap:after {
            opacity: 1;
        }
    }

    &--dark {
        .app-chip__wrap:before {
            opacity: 0.84;
        }
        .app-chip__wrap:after {
            background: black;
        }
        .app-chip__text {
            filter: brightness(4) saturate(0);
        }
        .app-chip__prefix {
            filter: brightness(4) saturate(0);
        }
        .app-chip__prefix:before {
            opacity: 0.2;
            filter: brightness(0);
        }
    }
}
</style>
